import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired once every box is filled. */
  onComplete?: (value: string) => void;
  length?: number;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Accessible name for the group, e.g. "6-digit verification code". */
  label?: string;
  className?: string;
}

/** One box per digit. Supports typing, backspace, arrow keys and pasting a full code. */
const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  onComplete,
  length = 6,
  error,
  disabled,
  autoFocus,
  label = 'Verification code',
  className,
}) => {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const [focused, setFocused] = useState<number | null>(null);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  useEffect(() => {
    if (autoFocus) inputs.current[Math.min(value.length, length - 1)]?.focus();
    // Only on mount / when re-enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, disabled]);

  const focusBox = (index: number) => inputs.current[Math.max(0, Math.min(index, length - 1))]?.focus();

  const commit = (next: string) => {
    const clean = next.replace(/\D/g, '').slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
    return clean;
  };

  const handleChange = (index: number, raw: string) => {
    const typed = raw.replace(/\D/g, '');
    if (!typed) return;
    // Typing over a box replaces it; extra characters (autofill) spill into the following boxes.
    const next = (value.slice(0, index) + typed).slice(0, length);
    const clean = commit(next + value.slice(index + typed.length));
    focusBox(Math.min(index + typed.length, clean.length));
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[index]) {
        commit(value.slice(0, index) + value.slice(index + 1));
      } else if (index > 0) {
        commit(value.slice(0, index - 1) + value.slice(index));
        focusBox(index - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const clean = commit(e.clipboardData.getData('text'));
    focusBox(clean.length);
  };

  return (
    <div role="group" aria-label={label} className={cn('tw-flex tw-gap-2', className)}>
      {digits.map((digit, i) => {
        const active = focused === i && !error;
        return (
          <input
            key={i}
            ref={(el) => (inputs.current[i] = el)}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={length}
            value={digit}
            disabled={disabled}
            aria-label={`Digit ${i + 1} of ${length}`}
            aria-invalid={error || undefined}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => {
              setFocused(i);
              e.target.select();
            }}
            onBlur={() => setFocused((f) => (f === i ? null : f))}
            className={cn(
              'tw-h-14 tw-w-12 tw-rounded-xl tw-bg-ds-surface tw-text-center tw-text-[22px] tw-font-semibold tw-leading-7 tw-outline-none tw-transition-colors',
              'tw-caret-ds-brand disabled:tw-cursor-not-allowed disabled:tw-opacity-60',
              error
                ? 'tw-border-2 tw-border-ds-danger-fg tw-text-ds-danger-fg'
                : active
                  ? 'tw-border-2 tw-border-ds-brand tw-text-ds-text-primary'
                  : digit
                    ? 'tw-border tw-border-ds-border-strong tw-text-ds-text-primary'
                    : 'tw-border tw-border-ds-border-default tw-text-ds-text-primary',
            )}
          />
        );
      })}
    </div>
  );
};

export default OtpInput;
